import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useAuth } from "@/auth/useAuth";
import { listFeatureFlags } from "@/api/featureFlags";
import { PageHeader } from "@/components/PageHeader";
import { RelatedLink } from "@/components/RelatedLink";
import { Timestamp } from "@/components/Timestamp";
import { ErrorState, LoadingState } from "@/components/states";
import { env } from "@/config/env";
import type { AppPlatform, AppVersionPolicyDoc } from "@/firestore/appVersionPolicies";
import { COLLECTIONS } from "@/firestore/collections";
import type { FeatureFlagDoc, FeatureFlagRecord } from "@/firestore/featureFlags";
import { useFirestoreQuery } from "@/firestore/hooks";
import type { WithDocId } from "@/firestore/types";
import { isPermissionError } from "@/lib/errors";
import { FeatureFlagDialog } from "./feature-flags/FeatureFlagDialog";
import { VendorActionDialog } from "./feature-flags/VendorActionDialog";
import {
  PLATFORMS,
  isOverdue,
  latestUpdatedAt,
  liveFlagFor,
  platformStateBadge,
  targetsBuilds,
  unknownKeys,
  vendorActionTitle,
  type VendorAction,
} from "./feature-flags/presentation";
import {
  FEATURE_FLAG_KEYS,
  FEATURE_FLAG_REGISTRY,
  FEATURE_FLAG_VENDORS,
  VENDOR_LABELS,
  keysForVendor,
  type FeatureFlagDefinition,
  type FeatureFlagKey,
  type FeatureFlagVendor,
} from "./feature-flags/registry";
import { useFeatureFlagCommand } from "./feature-flags/useFeatureFlagCommand";

const SAVED_MESSAGE = "Devices and the API pick it up within seconds.";
/** How often an idle page re-checks the time-based state (the overdue highlight). */
const CLOCK_TICK_MS = 30_000;

/**
 * The current time, refreshed every `intervalMs`: an expected-back time can pass while nothing
 * else re-renders the page, which is exactly when the overdue highlight matters.
 */
function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

function PlatformState({
  doc,
  platform,
  entry,
}: {
  doc: FeatureFlagDoc | null;
  platform: AppPlatform;
  entry: FeatureFlagDefinition;
}) {
  if (!doc && entry.appDefault) {
    // UI-only keys: users see the apps' own default, not the server's, until a row exists.
    return (
      <Tooltip
        label={`Server default: ${entry.defaultAvailable ? "on" : "off"} (superAdmin rule only). Writing a row replaces the apps' default on every bundle.`}
        multiline
        w={280}
        withArrow
      >
        <Badge color="gray" variant="outline" radius="sm" tt="none">
          Apps: {entry.appDefault.label} · no row
        </Badge>
      </Tooltip>
    );
  }
  if (!doc) {
    return (
      <Badge color="gray" variant="outline" radius="sm" tt="none">
        {entry.defaultAvailable ? "On" : "Off"} · code default
      </Badge>
    );
  }
  const badge = platformStateBadge(doc, platform);
  return (
    <Badge color={badge.color} variant="light" radius="sm" tt="none">
      {badge.label}
    </Badge>
  );
}

interface FlagRowProps {
  flagKey: FeatureFlagKey;
  doc: WithDocId<FeatureFlagDoc> | null;
  /** The admin `GET` row matching `doc`'s version, or null while it catches up / is unavailable. */
  record: FeatureFlagRecord | null;
  auditPending: boolean;
  canAct: boolean;
  now: number;
  onEdit(): void;
}

function FlagRow({ flagKey, doc, record, auditPending, canAct, now, onEdit }: FlagRowProps) {
  const entry = FEATURE_FLAG_REGISTRY[flagKey];
  const overdue = isOverdue(doc, now);
  const audit = (value: ReactNode) =>
    record ? (
      value
    ) : (
      <Text size="xs" c="dimmed">
        {auditPending ? "Refreshing…" : "—"}
      </Text>
    );
  return (
    <Table.Tr bg={overdue ? "var(--mantine-color-orange-light)" : undefined}>
      <Table.Td maw={320}>
        <Stack gap={4}>
          <Text size="sm" ff="monospace" fw={600}>
            {flagKey}
          </Text>
          <Text size="xs" c="dimmed">
            {entry.description}
          </Text>
          {entry.vendors.length > 0 ? (
            <Group gap={4}>
              {entry.vendors.map((vendor) => (
                <Badge key={vendor} size="xs" variant="default" radius="sm" tt="none">
                  {VENDOR_LABELS[vendor]}
                </Badge>
              ))}
            </Group>
          ) : null}
        </Stack>
      </Table.Td>
      {PLATFORMS.map((platform) => (
        <Table.Td key={platform}>
          <PlatformState doc={doc} platform={platform} entry={entry} />
        </Table.Td>
      ))}
      <Table.Td maw={240}>
        {doc?.message ? (
          <Text size="sm" lineClamp={3}>
            {doc.message}
          </Text>
        ) : (
          <Text span c="dimmed">
            —
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Stack gap={4}>
          <Timestamp value={doc?.expectedBackAt} absolute />
          {overdue ? (
            <Badge color="orange" variant="filled" radius="sm" tt="none" w="fit-content">
              Overdue
            </Badge>
          ) : null}
        </Stack>
      </Table.Td>
      <Table.Td maw={260}>
        {doc ? (
          <Stack gap={2}>
            <Timestamp value={doc.updatedAt} />
            {audit(<RelatedLink entity="user" id={record?.updatedBy} />)}
            {record ? (
              <Tooltip label={record.reason} multiline w={320} withArrow>
                <Text size="xs" c="dimmed" lineClamp={2}>
                  {record.reason}
                </Text>
              </Tooltip>
            ) : null}
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">
            Code default
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Button size="xs" variant={doc ? "default" : "light"} disabled={!canAct} onClick={onEdit}>
          {doc ? "Edit" : "Create"}
        </Button>
      </Table.Td>
    </Table.Tr>
  );
}

export function FeatureFlagsPage() {
  const { user, capabilities, getIdToken } = useAuth();
  const uid = user?.uid ?? "";
  const command = useFeatureFlagCommand();
  // Live values: the whole collection (≤ one row per registry key), no filters → no index.
  const live = useFirestoreQuery<FeatureFlagDoc>(COLLECTIONS.featureFlags);
  // Context for the review-build preset (≤ 2 docs).
  const appVersions = useFirestoreQuery<AppVersionPolicyDoc>(COLLECTIONS.appVersionPolicies);
  const revision = latestUpdatedAt(live.data);
  // Audit fields (`reason`, `updatedBy`) are not projected; refetch the admin GET whenever any
  // flag changes (AppVersionsPage pattern).
  const records = useQuery({
    queryKey: ["feature-flags", env.firebase.projectId, uid, revision],
    queryFn: async ({ signal }) => listFeatureFlags(await getIdToken(true), signal),
    enabled: !!uid,
    retry: false,
    gcTime: 0,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const [dialog, setDialog] = useState<{ key: FeatureFlagKey; version: string | null } | null>(
    null,
  );
  const [vendorDialog, setVendorDialog] = useState<{
    vendor: FeatureFlagVendor;
    action: VendorAction;
  } | null>(null);
  const now = useNow(CLOCK_TICK_MS);

  const crumbs = [{ label: "Platform" }, { label: "Feature flags" }];
  const dialogDoc = dialog ? liveFlagFor(live.data, dialog.key) : null;
  const strangers = unknownKeys(live.data);
  const emailReviewWindow = targetsBuilds(liveFlagFor(live.data, "emailPasswordSignIn"));

  return (
    <>
      <PageHeader
        title="Feature flags"
        subtitle="Switch app features on or off per platform and build, in real time. A key with no row runs on its code default. The API refuses a disabled feature with HTTP 503; the apps hide or disable its entry point."
        crumbs={crumbs}
        actions={
          <Button
            variant="default"
            size="xs"
            disabled={command.pending}
            onClick={() => {
              live.refresh();
              void records.refetch();
            }}
          >
            Refresh
          </Button>
        }
      />
      <Stack gap="lg">
        {env.isProd ? (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            Production. A flag change reaches every device and the API within seconds. Turning a
            feature off refuses it for real users on the affected builds until someone turns it back
            on — nothing switches back automatically.
          </Alert>
        ) : null}
        {env.isProd && emailReviewWindow ? (
          <Alert color="yellow" variant="light" title="Email sign-in is limited to a build range">
            <Code>emailPasswordSignIn</Code> targets specific builds. Once the review build is
            approved and before you release it, set it back to off or move it to the next review
            build. Keep Android on for the live build (Play app-access policy).
          </Alert>
        ) : null}
        {live.error ? (
          isPermissionError(live.error) ? (
            <Alert
              color="red"
              variant="light"
              icon={<IconAlertTriangle size={18} />}
              title="Access denied"
            >
              The <Code>featureFlags</Code> collection could not be read. Its Firestore rules may
              not be deployed in this environment yet — they ship with the identity backend release
              that adds feature flags.
            </Alert>
          ) : (
            <ErrorState error={live.error} />
          )
        ) : live.loading && live.data.length === 0 ? (
          <LoadingState />
        ) : (
          <>
            <Card withBorder radius="md" padding="sm">
              <Table.ScrollContainer minWidth={1100}>
                <Table verticalSpacing="sm" highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Feature</Table.Th>
                      <Table.Th>iOS</Table.Th>
                      <Table.Th>Android</Table.Th>
                      <Table.Th>Message</Table.Th>
                      <Table.Th>Expected back</Table.Th>
                      <Table.Th>Last update</Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {FEATURE_FLAG_KEYS.map((key) => {
                      const doc = liveFlagFor(live.data, key);
                      const match = doc
                        ? (records.data?.find((r) => r.id === doc.id) ?? null)
                        : null;
                      return (
                        <FlagRow
                          key={key}
                          flagKey={key}
                          doc={doc}
                          record={match && doc && match.updatedAt === doc.updatedAt ? match : null}
                          auditPending={records.isFetching || records.isPending}
                          canAct={command.canAct}
                          now={now}
                          onEdit={() => setDialog({ key, version: doc?.updatedAt ?? null })}
                        />
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Card>
            {strangers.length > 0 ? (
              <Alert color="gray" variant="light" title="Rows this console does not know">
                {strangers.join(", ")} — the apps and the API evaluate them, but they can't be
                edited here until the console's registry mirror is updated.
              </Alert>
            ) : null}
            <Card withBorder radius="md" padding="lg">
              <Stack gap="sm">
                <Stack gap={2}>
                  <Text fw={600}>Vendor maintenance</Text>
                  <Text size="sm" c="dimmed">
                    Pause switches every flag tagged with the vendor off on both platforms with one
                    reason; Resume switches them back on. Follow the vendor playbooks in
                    docs/FEATURE_FLAGS.md.
                  </Text>
                </Stack>
                {FEATURE_FLAG_VENDORS.map((vendor) => (
                  <Group key={vendor} justify="space-between" wrap="nowrap">
                    <Group gap="xs">
                      <Text fw={500} w={100}>
                        {VENDOR_LABELS[vendor]}
                      </Text>
                      {keysForVendor(vendor).map((key) => (
                        <Badge key={key} variant="default" radius="sm" tt="none">
                          {key}
                        </Badge>
                      ))}
                    </Group>
                    <Group gap="xs" wrap="nowrap">
                      {(["pause", "resume"] as const).map((action) => (
                        <Button
                          key={action}
                          size="xs"
                          variant="light"
                          color={action === "pause" ? "red" : "teal"}
                          disabled={!command.canAct}
                          onClick={() => setVendorDialog({ vendor, action })}
                        >
                          {vendorActionTitle(action, vendor)}
                        </Button>
                      ))}
                    </Group>
                  </Group>
                ))}
              </Stack>
            </Card>
          </>
        )}
        {records.error ? (
          <Alert color="orange" variant="light">
            Audit fields (reason, updated by) could not be loaded:{" "}
            {records.error instanceof Error ? records.error.message : String(records.error)}
          </Alert>
        ) : null}
      </Stack>
      {dialog ? (
        <FeatureFlagDialog
          key={`${dialog.key}:${dialog.version ?? "create"}`}
          flagKey={dialog.key}
          current={dialogDoc}
          expectedUpdatedAt={dialog.version}
          capabilities={capabilities}
          pending={command.pending}
          appVersions={appVersions.data}
          onSubmit={async (cmd) => {
            await command.execute(cmd);
            notifications.show({
              color: "teal",
              title: "Feature flag saved",
              message: SAVED_MESSAGE,
            });
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {vendorDialog ? (
        <VendorActionDialog
          key={`${vendorDialog.vendor}:${vendorDialog.action}`}
          vendor={vendorDialog.vendor}
          action={vendorDialog.action}
          docs={live.data}
          capabilities={capabilities}
          pending={command.pending}
          onRun={async (items) => {
            const results = await command.executeAll(items);
            const failed = results.filter((result) => !result.ok).length;
            notifications.show({
              color: failed ? "orange" : "teal",
              title: `${vendorActionTitle(vendorDialog.action, vendorDialog.vendor)}: ${results.length - failed} of ${results.length} flags saved`,
              message: SAVED_MESSAGE,
            });
            return results;
          }}
          onClose={() => setVendorDialog(null)}
        />
      ) : null}
    </>
  );
}
